import { limitBreach, netUnitPrice, resolveEntry } from '../../pricing';

/**
 * Le prix net qu'une entrée de politique donne à une quantité, avec son libellé, ou null
 * quand la politique ne décide rien. Le JavaScript ne fait que choisir le palier.
 */
export function suggestedNet(entry, quantity, publicSelling) {
    var target = resolveEntry(entry || null, quantity);

    if (!target || !target.field) {
        return null;
    }

    // Toute remise de la politique porte sur le prix public.
    return { net: netUnitPrice(publicSelling, target.discount), label: target.label };
}

/** L'entrée telle que le formulaire la garde, depuis une suggestion ou la route de tarification. */
export function pricingEntryOf(item) {
    if (!item || (!item.pricing && (item.discount === null || item.discount === undefined))) {
        return null;
    }

    return { pricing: item.pricing || null, discount: item.discount, discount_label: item.discount_label };
}

/**
 * La politique tarifaire sur une prévision d'étude, partagée par les prévisions hors lot
 * et celles d'un lot.
 *
 * L'étude n'a pas de remise : le prix de vente proposé est le prix net de la cascade, et
 * la marge en découle. Le prix commande tant qu'on ne le touche pas : changer le prix de
 * revient recalcule la marge, changer la marge ou le prix de vente écarte la politique.
 * Voir docs/DRI/Phase1/politique-tarifaire-etudes.md.
 */
export default {
    props: ['articleQuantities'],
    computed: {
        /**
         * Ce qui refuserait la ligne sur un devis : quantité minimale, puis plancher sur le
         * prix de vente, qui est déjà le net. Sur l'étude, un signal ; le blocage motivé
         * intervient à la transformation en devis.
         */
        limit_breach: function() {
            return limitBreach({
                article_id: this.prediction.article_id,
                is_title: this.prediction.is_title,
                quantity: this.prediction.quantity,
                unit: this.prediction.unit,
                amount: this.prediction.selling,
                discount: 0,
                minimum_order_quantity: this.prediction.minimum_order_quantity,
                minimum_selling_price: this.prediction.minimum_selling_price
            });
        },
        /**
         * Le palier s'apprécie ligne par ligne : six puis six unités ne déclenchent pas
         * celui de dix. Dit seulement quand cela peut coûter : un article à paliers, posé
         * sur plusieurs prévisions, lots compris.
         */
        split_quantity_notice: function() {
            if (!this.prediction.article_id || this.prediction.is_title) {
                return '';
            }

            var totals = (this.articleQuantities || {})[this.prediction.article_id];
            if (!totals || totals.count < 2) {
                return '';
            }

            var entry = this.prediction.pricing_entry;
            var hasTiers = entry && entry.pricing
                && (entry.pricing.tiers || []).some(function (tier) {
                    return parseFloat(tier.from) > 1;
                });

            if (!hasTiers) {
                return '';
            }

            return 'Cet article figure sur ' + totals.count + ' prévisions de cette étude, pour '
                + this.quantity(totals.quantity) + ' au total. Les quantités ne sont pas '
                + 'cumulées : le palier s\'apprécie ligne par ligne.';
        },
        article_total_quantity: function() {
            var totals = (this.articleQuantities || {})[this.prediction.article_id];

            return totals ? this.quantity(totals.quantity) : '';
        },
        has_suggested_selling: function() {
            return this.prediction.suggested_selling !== null && this.prediction.suggested_selling !== undefined;
        },
        selling_differs_from_suggestion: function() {
            if (!this.has_suggested_selling) {
                return false;
            }

            return Math.abs(parseFloat(this.prediction.selling || 0) - parseFloat(this.prediction.suggested_selling)) >= 0.0001;
        },
        on_policy_price: function() {
            return this.has_suggested_selling && !this.selling_differs_from_suggestion;
        },
        /**
         * La ligne suit encore la politique : au prix conseillé, ou au prix public tant
         * qu'aucun palier n'est atteint. Sans ce second cas, une ligne posée sous le premier
         * palier ne le prendrait jamais en montant la quantité.
         */
        follows_policy: function() {
            if (!this.prediction.pricing_entry) {
                return false;
            }

            var reference = this.has_suggested_selling ? this.prediction.suggested_selling : this.prediction.public_selling;

            return reference !== null && reference !== undefined
                && Math.abs(parseFloat(this.prediction.selling || 0) - parseFloat(reference)) < 0.0001;
        },
        /**
         * Prix repris à la main : différent du prix conseillé, ou du prix public quand la
         * politique ne décide rien. Une ligne dont on ignore encore le prix de référence
         * (importée, pas encore résolue) n'est pas tenue pour saisie à la main.
         */
        edited_by_hand: function() {
            var reference = this.has_suggested_selling ? this.prediction.suggested_selling : this.prediction.public_selling;

            return reference !== null && reference !== undefined
                && Math.abs(parseFloat(this.prediction.selling || 0) - parseFloat(reference)) >= 0.0001;
        },
        /** La marge cible de la catégorie, celle du cartouche de l'étude. */
        target_margin: function() {
            var targets = {
                1: this.defaultMarginWorkforce,
                2: this.defaultMarginPurchases,
                3: this.defaultMarginStocks,
                4: this.defaultMarginEquipments
            };
            var target = parseFloat(targets[this.prediction.type]);

            return Number.isFinite(target) ? target : null;
        },
        /**
         * Un prix issu de la politique tarifaire qui, au prix de revient du jour, sort
         * sous la marge cible mérite d'être vu. Une ligne reprise à la main porte une
         * marge choisie : rien à dire.
         */
        margin_below_target: function() {
            return this.on_policy_price
                && this.target_margin !== null
                && (parseFloat(this.prediction.margin) || 0) < this.target_margin;
        },
        margin_name: function() {
            return this.marginType == 2 ? 'marque' : 'marge';
        },
        selling_origin_label: function() {
            if (!this.has_suggested_selling) {
                return '';
            }

            var lines = [this.prediction.suggested_label];
            var suggested = this.currency(this.prediction.suggested_selling);

            if (this.selling_differs_from_suggestion) {
                lines.push('Ligne à ' + this.currency(this.prediction.selling) + ' au lieu de ' + suggested + ' (prix modifié)');
            } else {
                lines.push('PU ' + suggested + ', ' + this.margin_name + ' ' + this.rate(this.prediction.margin));
            }

            if (this.margin_below_target) {
                lines.push('Sous la ' + this.margin_name + ' cible (' + this.rate(this.target_margin) + ')');
            }

            return lines.join('\n');
        },
        margin_origin_label: function() {
            return this.margin_below_target ? this.selling_origin_label : '';
        }
    },
    methods: {
        tooltip: function(title) {
            return {
                title: title,
                trigger: 'hover',
                placement: 'top',
                boundary: 'window',
                customClass: 'ix-line-tooltip'
            };
        },
        /** Une quantité avec son unité, sans décimales inutiles : « 12 U » et non « 12,000 ». */
        quantity: function(value) {
            var formatted = (parseFloat(value) || 0).toFixed(3).replace(/\.?0+$/, '').replace('.', ',');

            return formatted + (this.prediction.unit ? ' ' + this.prediction.unit : '');
        },
        rate: function(value) {
            return (parseFloat(value) || 0).toFixed(2).replace(/\.?0+$/, '').replace('.', ',') + ' %';
        },
        /**
         * Ce qu'une suggestion apporte : la grille et les paliers du client, résolus par le
         * serveur. Retourne le prix net proposé à la quantité de la ligne, ou null quand la
         * politique ne décide rien.
         */
        applyPricingSuggestion: function(item) {
            return this.adoptPricingEntry(item.article_id ? pricingEntryOf(item) : null, item.selling, item.article_id ? item : null);
        },
        /**
         * Ce que l'article vaut chez le client, appris sans rien réécrire : une étude
         * rouverte compare son prix à celui de la politique, elle ne le remplace pas. Les
         * seuils de vente arrivent par le même chemin : ils suivent l'article.
         */
        adoptPricingEntry: function(entry, publicSelling, limits) {
            this.$set(this.prediction, 'pricing_entry', entry);
            this.$set(this.prediction, 'public_selling', publicSelling);
            this.$set(this.prediction, 'minimum_order_quantity', limits && limits.minimum_order_quantity !== undefined ? limits.minimum_order_quantity : null);
            this.$set(this.prediction, 'minimum_selling_price', limits && limits.minimum_selling_price !== undefined ? limits.minimum_selling_price : null);

            return this.suggestSellingFor(this.prediction.quantity);
        },
        /** Le palier atteint à cette quantité, sinon la grille : le JavaScript ne fait que choisir. */
        suggestSellingFor: function(quantity) {
            var suggested = suggestedNet(this.prediction.pricing_entry, quantity, this.prediction.public_selling);

            this.$set(this.prediction, 'suggested_selling', suggested ? suggested.net : null);
            this.$set(this.prediction, 'suggested_label', suggested ? suggested.label : null);

            return suggested ? suggested.net : null;
        },
        /** La ligne reprend le prix reçu et le tient pour celui de la politique. */
        applyPricingChange: function(entry, publicSelling, limits, selling) {
            this.adoptPricingEntry(entry, publicSelling, limits);
            this.prediction.selling = selling;
            this.compute('selling');
        },
        /** La ligne cesse d'appartenir à la politique ; les seuils, eux, suivent l'article. */
        clearPricingSuggestion: function() {
            this.$set(this.prediction, 'pricing_entry', null);
            this.$set(this.prediction, 'public_selling', null);
            this.$set(this.prediction, 'suggested_selling', null);
            this.$set(this.prediction, 'suggested_label', null);
        },
        /** Plus d'article sur la ligne : plus rien à en savoir. */
        clearArticleData: function() {
            this.clearPricingSuggestion();
            this.$set(this.prediction, 'minimum_order_quantity', null);
            this.$set(this.prediction, 'minimum_selling_price', null);
            // Plus d'article : plus de kit à déplier.
            this.$set(this.prediction, 'is_kit', false);
            this.$set(this.prediction, 'kit', null);
            this.$set(this.prediction, 'kit_open', false);
        },
        /** Un clic sur la pastille ramène la ligne au prix de la politique. */
        resumeSuggestedSelling: function() {
            if (!this.has_suggested_selling) {
                return;
            }

            this.prediction.selling = this.prediction.suggested_selling;
            this.compute('selling');
        },
        /**
         * Le palier suit la quantité tant que la ligne est au prix de la politique. Une ligne
         * reprise à la main garde son prix, et l'infobulle dit ce qu'elle aurait valu.
         */
        quantityChanged: function() {
            var wasOnPolicy = this.follows_policy;
            var net = this.prediction.pricing_entry ? this.suggestSellingFor(this.prediction.quantity) : null;

            if (wasOnPolicy) {
                // Sous le premier palier, la politique ne décide plus rien : retour au prix public.
                this.prediction.selling = net !== null ? net : this.prediction.public_selling;
                this.compute('selling');

                return;
            }

            this.compute();
        }
    }
};
